import { useNavigate } from '@tanstack/react-router'
import { selectCampaign, useCampaigns, type Campaign } from '../lib/campaign.tsx'
import { roleLabel } from '../lib/roles.ts'
import { useCampaignTotals } from '../lib/totals.ts'

/**
 * My campaigns (2026-09-30): the consultant's board. One card per campaign with
 * totals only — no voter is named here — then into a campaign to work in it.
 * The owner's words: "I check the stats, see how our canvassing is going, our
 * phoning … then I move to campaign B."
 */
export function MyCampaigns() {
  const { data, isPending } = useCampaigns()
  const campaigns = data?.campaigns ?? []

  return (
    <div>
      <h1 className="text-xl font-semibold tracking-tight">My campaigns</h1>
      <p className="mt-1 text-sm text-ink-muted">Totals only. Open a campaign to work in it.</p>

      {isPending ? <p className="mt-6 text-sm text-ink-muted">Loading…</p> : null}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {campaigns.map((c) => (
          <CampaignCard key={c.campaign_id} campaign={c} />
        ))}
      </div>
    </div>
  )
}

function CampaignCard({ campaign: c }: { campaign: Campaign }) {
  const navigate = useNavigate()
  // Reports are aggregate, and only a role that can count may run them.
  const t = useCampaignTotals(c.campaign_id, c.can_run_counts && c.may_report)
  const phones = c.dialer_seats > 0

  function open() {
    selectCampaign(c.campaign_id)
    void navigate({ to: '/overview' })
  }

  return (
    <button
      type="button"
      onClick={open}
      className="flex min-w-0 flex-col gap-3 rounded-lg border border-line bg-surface-raised p-4 text-left hover:border-brand-400"
    >
      <div>
        <div className="font-semibold leading-snug">{c.name}</div>
        <div className="text-xs text-ink-muted">
          {c.org_name} · {roleLabel(c.role)}
        </div>
      </div>

      <dl className="grid grid-cols-3 gap-2">
        <Figure label="doors knocked" value={c.walk_purchased ? t.doors : undefined} />
        <Figure label="calls made" value={phones ? t.calls : undefined} />
        <Figure label="supporters" value={t.supporters} />
      </dl>

      <p className="text-xs text-ink-muted">
        {!c.may_cut && c.may_report
          ? `Reporting only until ${c.report_until ?? '—'}`
          : c.election_date
            ? `Election ${c.election_date}`
            : 'No election date'}
        {' · '}
        {[c.walk_purchased ? 'Walk' : null, phones ? `Phones ×${c.dialer_seats}` : null, 'Lists']
          .filter(Boolean)
          .join(' · ')}
      </p>
    </button>
  )
}

/** undefined = not bought (shown as a dash with no number); null = loading. */
function Figure({ label, value }: { label: string; value: number | null | undefined }) {
  return (
    <div>
      <dt className="sr-only">{label}</dt>
      <dd className="font-mono text-lg font-semibold tabular-nums">
        {value === undefined || value === null ? '—' : value.toLocaleString()}
      </dd>
      <dd className="text-xs text-ink-muted">{label}</dd>
    </div>
  )
}
